import { Component, OnInit, OnDestroy, HostListener, Inject, DOCUMENT } from "@angular/core";
import { MessagingService } from "./shared/services/messaging.service";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { BUS_MESSAGE_KEY } from "./shared/constants/message-bus";
import { AuthenticationService } from "./shared/services/authentication.service";
import { Router, NavigationStart, NavigationEnd } from "@angular/router";
import { SharedService } from "./shared/services/shared.service";
import { AdobeAnalyticsService } from "./adobe-analytics.service";
import { User } from "./shared/constants/ascend-user-info";
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_LANGUAGE } from './shared/constants/language-constant';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { filter, take, takeUntil } from 'rxjs/operators';
import { InactivityTrackerService } from './shared/services/inactivity-tracker.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SessionTimeoutWarningComponent } from './shared/components/session-timeout-warning/session-timeout-warning.component';
import { CrossTabSyncService } from './shared/services/cross-tab-sync.service';

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  showLoader: BehaviorSubject<boolean>;
  showFileScanLoader: BehaviorSubject<boolean>;
  userData: BehaviorSubject<boolean>;
  langPreference: BehaviorSubject<boolean>;
  islogoutscreen: boolean = false;
  ispsg: boolean = false;
  isMaturityAssessment: boolean = false;
  isClientSurvey: boolean = false;
  currentroute: string = "";
  isTrainstop = false;
  isAmplifier = false;
  isContentRepo = false;
  isAuditLog: boolean = false;
  isExternalSignup: boolean = false;
  isLanguageSettings: boolean = false;
  hideNavigationBar = false;
  private subscriptions: Subscription[] = [];
  loginDisplay = false;
  isWindowLoaded: Boolean = false;
  private readonly _destroying$ = new Subject<void>();
  isUserRoleChanged: boolean = false;
  private warningDialogRef: any = null; // Phase 5: Reference to timeout warning dialog
  private warningShowCallback: (() => void) | null = null; // Phase 6: Callback reference for cleanup
  private warningDismissCallback: (() => void) | null = null; // Phase 6: Callback reference for cleanup

  @HostListener('window:load')
  onLoad() {
    this.isWindowLoaded = true;
    console.log('Window Loaded');
  }

  userDataObj: User;
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    @Inject(DOCUMENT) private document: Document,
    public router: Router,
    private messagingService: MessagingService,
    private authService: AuthenticationService,
    private sharedService: SharedService,
    private adobeAnalyticService: AdobeAnalyticsService,
    private translate: TranslateService,
    private inactivityTracker: InactivityTrackerService, // Phase 5: Inactivity tracker
    private dialog: MatDialog, // Phase 5: Material dialog for warning modal
    private crossTabSync: CrossTabSyncService // Phase 6: Cross-tab synchronization
  ) {
    this.messagingService.publish(BUS_MESSAGE_KEY.LOADER, true);
    this.currentroute = router.url;
    
    if (this.currentroute != "/logout" && !this.isClientSurvey) {
      this.handleMsalSubscriptions();
    }
    router.events.pipe(
      filter((event) => (event instanceof NavigationStart || event instanceof NavigationEnd))
    ).subscribe((event: NavigationStart | NavigationEnd) => {
      if (event instanceof NavigationStart) {
        this.isMaturityAssessment = event.url.includes('maturity-assessment');
        if (this.isMaturityAssessment) {
          this.loadStyle('ma-styles.css');
        }
        let result = null;
        const userRoleChangeSub = this.sharedService.userRoleChange$.subscribe(msg => {
          result = msg;
        });
        userRoleChangeSub.unsubscribe();
        if (result) {
          this.sharedService.setUserRoleChange(false)
          this.router.navigate(['/welcome']);
        }
      } else {
        this.ispsg = (event.url.includes("/psg/")) ? true : false;
        this.isTrainstop = (event.url.indexOf('/project/') === -1 && event.url.includes('/trainstops/')) ? true : false;
        this.isAmplifier = (event.url.indexOf('/amplifier-exchange') !== -1) ? true : false;
        this.isContentRepo = (event.url.indexOf('/content') !== -1) ? true : false;
        this.isAuditLog = (event.url.indexOf('/audit-log') !== -1) ? true : false;
        this.isExternalSignup = (event.url.indexOf('/external-user-signup') !== -1) ? true : false;
        this.isLanguageSettings = (event.url.indexOf('/language-settings') !== -1) ? true : false;
      }
    });
  }

  ngOnInit() {
    // Fix: If we land on /logout after MSAL login redirect, wait for MSAL to finish processing
    // then check if user is authenticated and redirect to welcome
    // This happens because MSAL's postLogoutRedirectUri is incorrectly set to /logout
    if (window.location.href.includes('/logout')) {
      console.log('[App] Detected /logout URL - waiting for MSAL to process redirect');
      
      // Wait for MSAL to finish processing the redirect (InteractionStatus.None means done)
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          take(1) // Only check once
        )
        .subscribe(() => {
          // Now check if user has accounts (is logged in)
          if (this.msalService.instance.getAllAccounts().length > 0) {
            console.log('[App] User logged in but on /logout - redirecting to /welcome');
            window.location.href = '/welcome';
          } else {
            console.log('[App] No active accounts - staying on /logout page');
          }
        });
    }
    
    this.subscriptions.push(
      this.sharedService.getHideNavigationBar().subscribe(data => {
        this.hideNavigationBar = data;
      }));
  }

  initialize() {
    this.setLoginDisplay();
    this.subscriptions.push(
      this.sharedService.getPreferredLanguage().subscribe((result: any) => {
        let lang = DEFAULT_LANGUAGE;
        if (result.data.preferredLanguage && result.data.enabledFlag) {
          lang = result.data.preferredLanguage;
          this.sharedService.setCurrentLanguage(lang?.languageId);
        }
        this.translate.use(lang.locale);
        this.messagingService.publish(BUS_MESSAGE_KEY.LANG_PREFERENCE, { lang, enabledFlag: result.data.enabledFlag, languages: result.data.supportedLanguages });
      })
    )

    this.langPreference = this.messagingService.getBus(BUS_MESSAGE_KEY.LANG_PREFERENCE);
    if (window.location.href.includes("logout")) this.islogoutscreen = true;
    if (window.location.href.includes("/client/survey")) this.isClientSurvey = true;

    this.showLoader = this.messagingService.getBus(BUS_MESSAGE_KEY.LOADER);
    this.showFileScanLoader = this.messagingService.getBus(BUS_MESSAGE_KEY.ANTI_VIRUS_SCAN_LOADER);
    this.userData = this.messagingService.getBus(BUS_MESSAGE_KEY.USER_DETAILS);

    this.messagingService.publish(BUS_MESSAGE_KEY.LOADER, false);

    // Phase 5: Start inactivity tracking after SSO/MSAL auto-login or manual login
    // Update loginDisplay status again to ensure it reflects current state
    this.setLoginDisplay();
    this.startInactivityTracking();
  }

  /**
   * Phase 5: Start inactivity tracking with warning and timeout callbacks
   * Called after SSO/MSAL auto-login completes or after manual login
   */
  private startInactivityTracking(): void {
    // Only start tracking if user is logged in and not on logout/client survey pages
    if (this.loginDisplay && !this.islogoutscreen && !this.isClientSurvey) {
      console.log('[Phase 5] Starting inactivity tracking for logged-in user (SSO/MSAL auto-login or manual login)');

      this.inactivityTracker.startTracking(
        // Warning callback - show modal 1 minute before timeout
        () => this.showTimeoutWarning(),
        // Timeout callback - logout user after timeout
        () => this.handleInactivityTimeout()
      );

      // Phase 6: Listen for warning show events from other tabs
      // Show warning modal in this tab when triggered in any tab
      this.warningShowCallback = () => {
        console.log('[Phase 6] Warning triggered in another tab, showing modal in this tab');
        this.showTimeoutWarning();
      };
      this.crossTabSync.onWarningShow(this.warningShowCallback);

      // Phase 6: Listen for warning dismiss events from other tabs
      // If user clicks Continue Session in another tab, close warning modal in this tab
      this.warningDismissCallback = () => {
        console.log('[Phase 6] Warning dismissed in another tab (user clicked Continue), closing modal in this tab');
        this.closeWarningModal();
      };
      this.crossTabSync.onWarningDismiss(this.warningDismissCallback);
    } else {
      console.log('[Phase 5] Skipping inactivity tracking:', {
        loginDisplay: this.loginDisplay,
        islogoutscreen: this.islogoutscreen,
        isClientSurvey: this.isClientSurvey
      });
    }
  }

  /**
   * Phase 5: Show timeout warning modal
   */
  private showTimeoutWarning(): void {
    // Prevent multiple dialogs
    if (this.warningDialogRef) {
      return;
    }

    console.log('[Phase 5] Showing timeout warning modal');

    // Phase 6: Broadcast warning show to other tabs so all tabs show modal simultaneously
    this.crossTabSync.broadcastWarningShow();

    this.warningDialogRef = this.dialog.open(SessionTimeoutWarningComponent, {
      width: '480px',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'session-timeout-dialog'
    });

    this.warningDialogRef.afterClosed().subscribe((result: string) => {
      this.warningDialogRef = null;

      if (result === 'continue') {
        // User clicked "Continue Session" - reset timers and broadcast to other tabs
        console.log('[Phase 5] User continued session, resetting timers');
        this.inactivityTracker.resetTimers();
        // Phase 6: Broadcast warning dismiss to close modals in other tabs
        this.crossTabSync.broadcastWarningDismiss();
      } else if (result === 'timeout') {
        // Timer expired - logout user
        console.log('[Phase 5] Warning timeout expired, logging out');
        this.handleInactivityTimeout();
      }
    });
  }

  /**
   * Phase 6: Close warning modal (called when user clicks Continue Session in another tab)
   */
  private closeWarningModal(): void {
    if (this.warningDialogRef) {
      console.log('[Phase 6] Closing warning modal because user clicked Continue Session in another tab');
      this.warningDialogRef.close('dismissed-by-other-tab');
      this.warningDialogRef = null;
    }
  }

  /**
   * Phase 5: Handle inactivity timeout - logout user
   */
  private handleInactivityTimeout(): void {
    console.log('[Phase 5] Inactivity timeout - logging out user');

    // Stop tracking
    this.inactivityTracker.stopTracking();

    // Close warning dialog if open
    if (this.warningDialogRef) {
      this.warningDialogRef.close();
      this.warningDialogRef = null;
    }

    // Trigger logout with 'timeout' reason (Phase 2 integration)
    this.authService.logoutWithCleanup('timeout');
  }

  handleMsalSubscriptions() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => {
          return msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        }
        ),
      )
      .subscribe((result: EventMessage) => {
        !this.loginDisplay && this.authService.initializeAuth();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => {
          return status === InteractionStatus.None
        }),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.authService.initializeAuth();
        this.initialize();
      });

    // Phase 5: Subscribe to USER_DETAILS to start inactivity tracking for both SSO and local auth
    this.messagingService.subscribe(BUS_MESSAGE_KEY.USER_DETAILS, (userData) => {
      if (userData && !this.islogoutscreen && !this.isClientSurvey) {
        console.log('[Phase 5] USER_DETAILS received, starting inactivity tracking');
        // Update loginDisplay to true since we have user data
        this.loginDisplay = true;
        this.startInactivityTracking();
      }
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  @HostListener('click', ['$event.target']) async onClick(event) {
    this.messagingService.subscribe(BUS_MESSAGE_KEY.USER_DETAILS, (data) => {
      this.userDataObj = data;
      this.userDataObj.userId = data?.ssoUser?.userPrincipalName ?? data.userId;
    })
    if (event.outerText != null && event.outerText != '' && event.outerText != undefined && this.userDataObj.userId != null) {
      this.adobeAnalyticService.setApplicationName("Ascend");
      this.adobeAnalyticService.setPageName(this.router?.url);
      this.adobeAnalyticService.setUserId(this.userDataObj.userId.split('@')[0].toString());
      this.adobeAnalyticService.setButtonName(event.outerText);
      const marketSolutionName = this.adobeAnalyticService.getMarketSolutionName();

      if (marketSolutionName) {
          await this.adobeAnalyticService.trackMarketSolutionEvent();
          this.adobeAnalyticService.setMarketSolutionName('');
      } else {
        this.adobeAnalyticService.trackNavigationEventAction();
      }
    }
  }

  loadStyle(styleName: string) {
    const head = this.document.getElementsByTagName('head')[0];

    let themeLink = this.document.getElementById(
      'ma-theme'
    ) as HTMLLinkElement;
    if (!themeLink) {
      const style = this.document.createElement('link');
      style.id = 'ma-theme';
      style.rel = 'stylesheet';
      style.href = `${styleName}`;

      head.appendChild(style);
    }
  }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.authService?.cleanListerners();
    this.subscriptions?.forEach((sub) => {
      sub.unsubscribe();
    });

    // Phase 5: Stop inactivity tracking on component destroy
    this.inactivityTracker.stopTracking();

    // Phase 5: Close warning dialog if open
    if (this.warningDialogRef) {
      this.warningDialogRef.close();
      this.warningDialogRef = null;
    }

    // Phase 6: Clear warning callback references
    // Note: CrossTabSyncService.ngOnDestroy() will clear the actual callback arrays
    // We just null our references here for garbage collection
    this.warningShowCallback = null;
    this.warningDismissCallback = null;
  }
}
